import moment from "moment";
import { Removable } from "./Utils";

// ---------- helpers: aplicar filtro Amplify en cliente ----------
export type AmplifyScalarOps =
    | "eq" | "ne"
    | "contains" | "notContains" | "beginsWith"
    | "gt" | "gte" | "lt" | "lte"
    | "dateGt" | "dateGte" | "dateLt" | "dateLte"
    | "between" | "in" | "notIn" | "expireDataLte";

export type AmplifyFieldCondition = Partial<Record<AmplifyScalarOps, any>>;
export type AmplifyFilter =
    | { and?: AmplifyFilter[]; or?: AmplifyFilter[]; not?: AmplifyFilter }
    | Record<string, AmplifyFieldCondition>; // { fieldName: { op: value, ... } }

export const coerceNum = (v: any): number | null => {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
};

export const matchesField = (value: any, cond: AmplifyFieldCondition, item: any): boolean => {
    for (const [op, target] of Object.entries(cond) as [AmplifyScalarOps, any][]) {
        switch (op) {
            case "eq":
                if (value !== target) return false;
                break;
            case "ne":
                if (value === target) return false;
                break;
            case "contains": {
                const vs = value?.toString().toLowerCase() ?? "";
                const ts = target?.toString().toLowerCase() ?? "";
                if (!vs.includes(ts)) return false;
                break;
            }
            case "notContains": {
                const vs = value?.toString().toLowerCase() ?? "";
                const ts = target?.toString().toLowerCase() ?? "";
                if (vs.includes(ts)) return false;
                break;
            }
            case "beginsWith": {
                const vs = value?.toString().toLowerCase() ?? "";
                const ts = target?.toString().toLowerCase() ?? "";
                if (!vs.startsWith(ts)) return false;
                break;
            }
            case "gt": {
                const v = coerceNum(value), t = coerceNum(target);
                if (v === null || t === null || !(v > t)) return false;
                break;
            }
            case "gte": {
                const v = coerceNum(value), t = coerceNum(target);
                if (v === null || t === null || !(v >= t)) return false;
                break;
            }
            case "lt": {
                const v = coerceNum(value), t = coerceNum(target);
                if (v === null || t === null || !(v < t)) return false;
                break;
            }
            case "lte": {
                const v = coerceNum(value), t = coerceNum(target);
                if (v === null || t === null || !(v <= t)) return false;
                break;
            }
            case "dateGt": {
                const valueDate = moment(value.substring(0, 10))
                const targetDate = moment(target)
                return !valueDate.isValid() || !targetDate.isValid() || valueDate.isAfter(targetDate)
            }
            case "dateGte": {
                const valueDate = moment(value.substring(0, 10))
                const targetDate = moment(target)
                return !valueDate.isValid() || !targetDate.isValid() || valueDate.isSameOrAfter(targetDate)
            }
            case "dateLt": {
                const valueDate = moment(value.substring(0, 10))
                const targetDate = moment(target)
                return !valueDate.isValid() || !targetDate.isValid() || valueDate.isBefore(targetDate)
            }
            case "dateLte": {
                const valueDate = moment(value.substring(0, 10))
                const targetDate = moment(target)
                return !valueDate.isValid() || !targetDate.isValid() || valueDate.isSameOrBefore(targetDate)
            }
            case "expireDataLte": {
                const { endDate = Date(), dateField = "createdAt" } = target
                const targetExpiratedDate = moment(endDate).subtract(value, "days");
                const valueDate = moment(`${item[dateField]}`.substring(0, 10))
                return !valueDate.isValid() || !targetExpiratedDate.isValid() || valueDate.isSameOrBefore(targetExpiratedDate)
            }

            case "between": {
                const [min, max] = target ?? [];
                const v = coerceNum(value), mi = coerceNum(min), ma = coerceNum(max);
                if (v === null || mi === null || ma === null || v < mi || v > ma) return false;
                break;
            }
            case "in": {
                if (!Array.isArray(target) || !target.includes(value)) return false;
                break;
            }
            case "notIn": {
                if (Array.isArray(target) && target.includes(value)) return false;
                break;
            }
            default:
                // operador no soportado: lo consideramos no-match
                return false;
        }
    }
    return true;
};

export const matchesFilterObject = (item: any, obj: Record<string, AmplifyFieldCondition>): boolean => {
    // Cada key es un campo con condición(es)
    for (const [field, cond] of Object.entries(obj)) {
        // si la llaves es un object, ne ese caso pasa la condicion al siguiente nivel  (p.ej., "client":{zoneId:{eq:{1234}}}, etc.)
        if (item[field] != null && typeof item[field] == "object") {
            return matchesFilterObject(item[field], cond)
        }
        // soporta llaves simples al nivel raíz (p.ej., "providerId", "numero", etc.)
        if (!matchesField((item as any)[field], cond, item)) return false;
    }
    return true;
};

export const matchesAmplifyFilter = (item: any, filter?: AmplifyFilter): boolean => {
    if (!filter || (typeof filter === "object" && Object.keys(filter).length === 0)) return true;

    // combinadores lógicos
    if ("and" in filter && Array.isArray(filter.and)) {
        return filter.and.every(f => matchesAmplifyFilter(item, f));
    }
    if ("or" in filter && Array.isArray(filter.or)) {
        return filter.or.some(f => matchesAmplifyFilter(item, f));
    }
    if ("not" in filter && filter.not) {
        return !matchesAmplifyFilter(item, filter.not);
    }

    // condiciones de campos
    return matchesFilterObject(item, filter as Record<string, AmplifyFieldCondition>);
};

export const errorHandler = (error: any, message: string = "Api Error") => {
    console.log(message, error);
}

export const toLocaleTime = (utcTime: moment.MomentInput, format: string = "DD/MM/YYYY") => {
    const time = moment.utc(utcTime);
    return time.local().format(format);
}

export const getCurrentDateTime = (outputFormat: string = "YYYY-MM-DD HH:mm:ss") => {
    const date = moment()
    return date.format(outputFormat)
}

export const formatDate = (fecha: string | Date | null | undefined, inputFormat: string = "YYYY-MM-DD", outputFormat: string = "DD-MM-YYYY") => {
    if (fecha) {
        const date = moment(fecha, inputFormat)
        return date.format(outputFormat)
    }
    return ""
}

/**
 * Elimina propiedades null de un objeto.
 * 
 * @param obj - Objeto original
 *  * @param removeNulls - Si es true, también eliminará propiedades con valor null
 * @returns Nuevo objeto sin las propiedades indicadas
 */
export function removeNullProperties<
    T extends object,
    P extends readonly (keyof T | string)[]
>(
    obj: T,
): Removable<T, P> {
    const out: any = {};
    for (const [k, v] of Object.entries(obj) as [keyof T, T[keyof T]][]) {
        if (v === null) continue;
        out[k] = v;
    }
    return out;
}